import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { UnitsVisibility } from '../types/units-visibility';
import { REACT_APP_GEOJSON_LOCATION, REACT_APP_API_BASE } from '../../../config';
import { createMapService } from '../../../services/mapService';
import { createUnitsService } from '../../../services/unitsService';
import { parseSelectedLayers } from '../helpers/utils/zip-code-parser';
import { convertFeaturesToLayerFormat } from '../helpers/utils/convert-features-to-layer-format';
import { getSelectedPolygonFeatures } from '../helpers/districts/district-loader';
import { renderSelectionTypeFeatures, preloadLayer } from '../helpers/map/selection-type-operations';
import { loadInitialData } from '../helpers/districts/initialization';
import { applyDistrictFiltersAndCalculate } from '../helpers/districts/sales-districts-operations';
import * as mapOps from '../helpers/map/map-operations';
import * as areaOps from '../helpers/drawing/area-operations';
import * as drawingOps from '../helpers/drawing/drawing-operations';
import * as unitsOps from '../helpers/units/units-operations';
import * as districtCache from '../helpers/districts/district-cache';
import * as salesDistrictsOps from '../helpers/districts/sales-districts-operations';
import { createAreasForSave } from '../helpers/save/save-data-operations';
import { createHouseMarker } from '../helpers/drawing/drawing-operations';

/**
 * Build a minimal turfPolygons object containing only the entries
 * referenced by selectedAreas. This avoids serializing the entire
 * ~10k-entry turfPolygons via postMessage (structured clone).
 */
function buildMinimalTurfPolygons(selectedAreas) {
    const minimal = {};
    for (const feature of selectedAreas) {
        const layerType = feature.properties?.type;
        if (!layerType) continue;
        if (!minimal[layerType]) minimal[layerType] = [];
        const entry = mapOps.getTurfEntryById(layerType, feature.id);
        if (entry) minimal[layerType].push(entry);
    }
    return minimal;
}

/**
 * Custom hook för district map
 * @param {Object} props - Component props
 * @returns {Object} { state, handlers }
 */
export function useDistrictMap(props) {
    const { type, match, fetch, history } = props;
    
    // Services
    const mapServiceRef = useRef(null);
    const unitsServiceRef = useRef(null);
    const houseMarkerRef = useRef(null);
  
    // Initialize services
    if (!mapServiceRef.current) {
        mapServiceRef.current = createMapService(fetch);
    }
    if (!unitsServiceRef.current) {
        unitsServiceRef.current = createUnitsService(fetch);
    }
  
    // Debounce refs
    const createAreasForSaveDebouncedRef = useRef(null);
    const activeWorkersRef = useRef(new Map());
    const loadedDistrictIdRef = useRef(null);
    const loadedScenarioIdRef = useRef(null);

    useLayoutEffect(() => {
        loadedDistrictIdRef.current = null;
        loadedScenarioIdRef.current = null;
    }, [match.params.id, match.params.scenarioId]);
    
     // React state (endast UI-relaterat — allDistricts lever i districtCache, inte här)
     const [state, setState] = useState({
        isLoading: true,
        uiStates: {
            unitsVisibility: UnitsVisibility.ALL,
            undoIsActive: false,
            mapIsLoading: true,
            isSaving: false,
            isFinishedSaving: false,
            isLoadingMarkers: false
        }
    });
    
    // Initialize district cache dependencies
    useEffect(() => {
        districtCache.setDependencies({
            mapService: mapServiceRef.current,
            unitsService: unitsServiceRef.current,
            type,
            scenarioId: match.params.scenarioId,
            fetchUnitsCallback: (t) => unitsServiceRef.current.fetchUnits(t)
        });
    }, [match.params.scenarioId]);
    
    // Exponera externa handlers för t.ex. unit-popup så att den kan synca panelen från cachen
    useEffect(() => {
        mapOps.setExternalHandlers({
            syncAllDistrictsFromCache: () => {
                districtCache.notifySubscribers();
            }
        });
    }, []);
  
    /**
     * Hanterar när selectedAreas ändras i map state
     * Uppdaterar beräkningar och React state
     * @param {Array} selectedAreas - Nya selected areas
     */
    /**
     * Applicera ändringar på distriktet (selectedAreas eller filters)
     * Beräknar filteredUnits, calculations, sparar i cache och renderar units
     * @param {Array} selectedAreas - Selected areas (GeoJSON features)
     * @param {boolean} rerenderUnits - Om units ska renderas om
     * @param {boolean|null} forceIsDirty - Tvinga isDirty till true/false, eller null för auto-beräkning
     * @param {boolean} forceFullRebuild - Om true, tvinga full ombyggnad av markers (vid filter/visibility/distriktsbyte)
     */
    const applyDistrictChanges = async (selectedAreas, rerenderUnits = false, forceIsDirty = null, forceFullRebuild = false) => {
        const districtState = districtCache.get(match.params.id);

        const classifications = districtCache.getClassifications(match.params.scenarioId);

        const { filteredUnits, calculations } = applyDistrictFiltersAndCalculate({ ...districtState, selectedAreas }, classifications);

        const { aregion, county, municipality, postalCodes, localities } = convertFeaturesToLayerFormat(selectedAreas);
        
        const isDirty = forceIsDirty !== null 
            ? forceIsDirty 
            : (districtState.isDirty === true ||
               (districtState.selectedAreas && districtState.selectedAreas.length !== selectedAreas.length) ||
               (districtState.selectedAreas?.map(a => a?.properties?.id).sort().join(',') !== 
                selectedAreas?.map(a => a?.properties?.id).sort().join(',')));
        
        districtCache.saveCurrent({ 
            ...districtState, 
            ...calculations,
            filteredUnits,
            isDirty,
            isScheduledForSpatialRebuild: true,
            selectedAreas,
            aregion,
            county,
            municipality,
            postalCodes,
            localities,
        });

        unitsOps.renderUnits(
            filteredUnits,
            selectedAreas,
            fetch,
            districtState.salesDistrictFilters,
            districtState.allUnits,
            rerenderUnits,
            calculations.units,
            forceFullRebuild
        );
        
        mapOps.applyStyles();
    };
    
      /**
     * Bygg spatialGeoJson för ett distrikt i bakgrunden (används för flera distrikt samtidigt)
     * Denna version avbryter inte tidigare workers och kan köras parallellt
     */
      const buildSpatialInfoForDistrictInBackground = (district, selectedAreas, cb) => {
        const districtId = district.id;
        const turfPolygons = buildMinimalTurfPolygons(selectedAreas);
        
        // Kolla om det redan finns en aktiv worker för detta distrikt
        if (activeWorkersRef.current.has(districtId)) {
            console.log(`[buildSpatialInfoForDistrictInBackground] Worker already running for district ${districtId}`);
            return;
        }
        
        const worker = new Worker(new URL('../helpers/save/worker.js', import.meta.url));
        activeWorkersRef.current.set(districtId, worker);
        
        worker.onmessage = (message) => {
            const { type, spatialGeoJson } = message.data;
            
            if (type === 'save') {
                try {
                    // Hämta uppdaterad district state
                    const districtState = districtCache.get(districtId);
                    if (!districtState) {
                        console.warn(`[buildSpatialInfoForDistrictInBackground] District ${districtId} not found in cache`);
                        return;
                    }
                    
                    let geoJsonString = null;
                    if (spatialGeoJson) {
                        spatialGeoJson.properties = {
                            ...spatialGeoJson.properties,
                            color: districtState.district?.color || district.color || '#ff0000',
                            name: districtState.district?.name || district.name || ''
                        };
                        geoJsonString = [spatialGeoJson];
                    }
                    
                    // Anropa callback
                    cb({ ...districtState, ...district }, { spatialGeoJson: geoJsonString });
                } catch (error) {
                    console.error('buildSpatialInfoForDistrictInBackground, error', error);
                } finally {
                    // Ta bort workern från activeWorkers när den är klar
                    activeWorkersRef.current.delete(districtId);
                    worker.terminate();
                }
            }
        };
        
        worker.postMessage({ 
            selectedAreas, 
            turfPolygons
        });
    };

    /**
     * Bygg spatialGeoJson för alla distrikt i bakgrunden
     * Körs när ett distrikt laddas för att säkerställa att alla distrikt har uppdaterad spatialGeoJson
     * @param {Function} cb - Callback som anropas för varje distrikt när det är klart
     * @param {Function} onAllComplete - Callback som anropas när alla distrikt är klara
     */
    const buildSpatialGeoJsonForAllDistricts = (cb, onAllComplete) => {
        const allDistricts = districtCache.getAllDistrictsForScenario();
        
        // Filtrera distrikt som behöver spatialGeoJson byggas
        const districtsToBuild = allDistricts.filter(district => {
            // Måste ha selectedAreas
            // Hoppa över om det redan finns en aktiv worker för detta distrikt
            return district.selectedAreas && district.selectedAreas.length > 0 &&
                   district.isScheduledForSpatialRebuild &&
                   !activeWorkersRef.current.has(district.id); // Hoppa över om worker redan körs
        });
        
        if (districtsToBuild.length === 0) {
            // Om inga distrikt behöver byggas, anropa onAllComplete direkt
            if (onAllComplete) {
                onAllComplete();
            }
            return;
        }

        // Räkna antalet distrikt som ska byggas
        let completedCount = 0;
        const totalCount = districtsToBuild.length;

        // Bygg spatialGeoJson för alla dirty distrikt
        districtsToBuild.forEach(district => {
            buildSpatialInfoForDistrictInBackground(
                district,
                district.selectedAreas,
                (updatedDistrict, { spatialGeoJson }) => {
                    // Anropa callback för varje distrikt
                    if (cb) {
                        cb(updatedDistrict, { spatialGeoJson });
                    }
                    
                    // Räkna ner när varje worker är klar
                    completedCount++;
                    
                    // När alla workers är klara, anropa onAllComplete
                    if (completedCount === totalCount && onAllComplete) {
                        onAllComplete();
                    }
                }
            );
        });
    };
    
    
    /**
     * Hämta selected areas för distriktet
     * Laddar först alla nödvändiga layers så att turfPolygons finns tillgängliga
     */
    const getSelectedAreasForDistrict = async (savedState, parsedLayers, parsedLayersKeys) => {
        if (savedState.selectedAreasFeatures && savedState.selectedAreasFeatures.length > 0) {
            return savedState.selectedAreasFeatures;
        }
        
        // Ladda alla layers som behövs för att hitta selected areas
        // Detta säkerställer att turfPolygons finns för alla layers
        for (let i = 0; i < parsedLayersKeys.length; i++) {
            const layerType = parsedLayersKeys[i];
            if (parsedLayers[layerType].length > 0) {
                // Ladda layer om den inte redan är laddad
                if (!mapOps.hasLayerType(layerType)) {
                    await preloadLayer(layerType, mapOps, fetchGeoJSON);
                }
            }
        }
        
        const selectedAreasToUse = [];
        for (let i = 0; i < parsedLayersKeys.length; i++) {
            const layerType = parsedLayersKeys[i];
            if (parsedLayers[layerType].length > 0) {
                const selectedAreas = getSelectedPolygonFeatures(
                    parsedLayers[layerType],
                    layerType,
                    mapOps
                );
                selectedAreasToUse.push(...selectedAreas);
            }
        }
        return selectedAreasToUse;
    };
    
    /**
     * Beräkna selection type från parsed layers keys
     * @param {Array} parsedLayersKeys - Array av layer keys
     * @returns {string} Selection type
     */
    const calculateSelectionType = (parsedLayersKeys) => {
        return (parsedLayersKeys.length > 0 ? parsedLayersKeys[0] : 'clear');
    };
    
    /**
     * Rendera sales districts
     */
    const renderSalesDistricts = (currentDistrictId) => {
        const allDistricts = districtCache.getAllDistrictsForScenario();
        salesDistrictsOps.show(allDistricts, currentDistrictId);
    };

    const deleteHomeMarkerForDistrict = () => {
        if (houseMarkerRef.current) {
            houseMarkerRef.current.setMap(null);
            houseMarkerRef.current = null;
        }
    };

    const handleChangeHouseMarker = (marker) => {
        const district = districtCache.get(match.params.id);
        if (!district) {
            return;
        }

        if (marker) {
            // Marker skapades eller flyttades
            if (houseMarkerRef.current !== marker) {
                deleteHomeMarkerForDistrict();
            }

            houseMarkerRef.current = marker;
            const homeMarkerGeoJson = marker.getGeoJSON();
            
            districtCache.saveCurrent({
                ...district,
                homeMarkerGeoJson: homeMarkerGeoJson || null,
                isDirty: true
            });
        } else {
            // Marker togs bort (användaren klickade på stäng-knappen)
            // Ta bort home marker från homeMarkerInstances Map först (för att säkerställa att rätt distrikt tas bort)
            salesDistrictsOps.removeHomeMarker(district.id);
            
            // Ta bort home marker från ref om den tillhör det aktuella distriktet
            if (houseMarkerRef.current) {
                houseMarkerRef.current.setMap(null);
                houseMarkerRef.current = null;
            }
            
            // Uppdatera cache så att homeMarkerGeoJson är null
            districtCache.saveCurrent({
                ...district,
                homeMarkerGeoJson: null,
                isDirty: true
            });
        }

        districtCache.notifySubscribers();
    }
    
    
    /**
     * Skapa home marker för det aktuella distriktet
     */
    const createHomeMarkerForDistrict = (district) => {
        if (!district.homeMarkerGeoJson) {
            return;
        }
        
        const maps = mapOps.getMaps();
        const map = mapOps.getMap();

        deleteHomeMarkerForDistrict();
        
        const homeMarkerGeoJson = district.homeMarkerGeoJson;
        const center = new maps.LatLng(homeMarkerGeoJson.geometry.coordinates[1], homeMarkerGeoJson.geometry.coordinates[0]);
            
        const marker = createHouseMarker({
            maps,
            map,
            center,
            draggable: false,
            onMarkerMoved: (movedMarker) => {
                // Uppdatera cache när markern flyttas
                const currentDistrict = districtCache.get(match.params.id);
                if (currentDistrict) {
                    const newHomeMarkerGeoJson = movedMarker.getGeoJSON();
                    districtCache.saveCurrent({
                        ...currentDistrict,
                        homeMarkerGeoJson: newHomeMarkerGeoJson || null,
                        isDirty: true
                    });
                    
                    districtCache.notifySubscribers();
                }
            },
            onClose: handleChangeHouseMarker,
            showCloseButton: true,
            style: {
                backgroundColor: district.color
            }
        });
        
        houseMarkerRef.current = marker;
        
        // Spara även i homeMarkerInstances Map så att den tas bort korrekt när renderSalesDistricts anropas
        salesDistrictsOps.addHomeMarker(district.id, marker);
    };
    
    /**
     * Sätt kartan till default position
     */
    const setDefaultMapPosition = () => {
        if (!mapOps.isInitialized()) {
            return;
        }
        
        const map = mapOps.getMap();
        const maps = mapOps.getMaps();
        
        const defaultCenter = {
            lat: 59.3273056,
            lng: 17.8390038
        };
        const defaultZoom = 6;
        
        map.setCenter(new maps.LatLng(defaultCenter.lat, defaultCenter.lng));
        map.setZoom(defaultZoom);
    };
    
    /**
     * Sätt upp zoom till distriktet
     */
    const setupDistrictZoom = async (parsedLayers, parsedLayersKeys) => {
        const performZoom = () => {
            if (parsedLayersKeys.length === 1) {
                const districtType = parsedLayersKeys[0];
                const idsToZoom = parsedLayers[districtType];
                mapOps.zoomToDistrict(idsToZoom);
            } else if (parsedLayersKeys.length > 1) {
                const allDistrictIds = [];
                parsedLayersKeys.forEach(layerType => {
                    if (parsedLayers[layerType] && parsedLayers[layerType].length > 0) {
                        allDistrictIds.push(...parsedLayers[layerType]);
                    }
                });
                mapOps.zoomToDistrict(allDistrictIds);
            } else {
                mapOps.zoomToDistrict();
            }
        };
        
        // Vänta på map idle event
        let idleListener = null;
        let zoomPerformed = false;
        
        const performZoomOnce = () => {
            if (!zoomPerformed) {
                zoomPerformed = true;
                if (idleListener) {
                    const maps = mapOps.getMaps();
                    maps.event.removeListener(idleListener);
                }
                performZoom();
            }
        };
        
        const maps = mapOps.getMaps();
        const map = mapOps.getMap();
        idleListener = maps.event.addListener(
            map,
            'idle',
            performZoomOnce
        );
        
        // Trigga idle event
        if (map.getZoom() !== undefined) {
            const currentZoom = map.getZoom();
            map.setZoom(currentZoom + 0.0001);
            map.setZoom(currentZoom);
        }
    };
    
    /**
     * Create areas for save
     */
    const createAreasForSaveCallback = () => {
        const district = districtCache.get(match.params.id);
        const areas = district?.selectedAreas || [];
        createAreasForSave(
            areas,
            buildMinimalTurfPolygons(areas),
            (saveData) => {
                setState(prevState => ({
                    ...prevState,
                    selectedAreas: saveData
                }));
            }
        );
    };
    
    /**
     * Select or toggle an area
     * @param {Object} area - Area feature
     * @param {boolean} isToggle - Om area ska togglas eller bara väljas
     */
    const selectArea = async (area, isToggle = true) => {
        const handleChange = (selectedAreas) => {
            applyDistrictChanges(selectedAreas, true);
        };
        
        if (isToggle) {
            areaOps.toggleArea(area, handleChange);
        } else {
            areaOps.selectArea(area, handleChange);
        }
        
        // Debounce createAreasForSave för att undvika onödiga Web Worker-anrop vid snabb val
        if (createAreasForSaveDebouncedRef.current) {
            clearTimeout(createAreasForSaveDebouncedRef.current);
        }
        createAreasForSaveDebouncedRef.current = setTimeout(() => {
            createAreasForSaveCallback();
            districtCache.notifySubscribers();
        }, 300);
    };

    /**
     * Batch-add multiple areas at once (used by drawing tools).
     * Merges all new features into selectedAreas in one pass, then runs
     * applyDistrictChanges exactly once.
     * @param {Array} features - Array of GeoJSON features to add
     */
    const selectAreasBatch = async (features) => {
        if (!features || features.length === 0) return;

        const currentDistrictId = districtCache.getCurrentDistrictId();
        if (!currentDistrictId) return;

        const district = districtCache.get(currentDistrictId);
        if (!district) return;

        const existingAreas = district.selectedAreas || [];
        const existingIds = new Set(existingAreas.map(a => a.properties?.id));

        const newAreas = features.filter(f => f.properties?.id && !existingIds.has(f.properties.id));
        if (newAreas.length === 0) return;

        const mergedSelectedAreas = [...existingAreas, ...newAreas];

        districtCache.saveCurrent({
            ...district,
            selectedAreas: mergedSelectedAreas,
            isDirty: true
        });

        await applyDistrictChanges(mergedSelectedAreas, true);

        if (createAreasForSaveDebouncedRef.current) {
            clearTimeout(createAreasForSaveDebouncedRef.current);
        }
        createAreasForSaveDebouncedRef.current = setTimeout(() => {
            createAreasForSaveCallback();
            districtCache.notifySubscribers();
        }, 300);
    };
    
    /**
     * Fetch GeoJSON
     */
    const fetchGeoJSON = async (geoJSONType) => {
        const geoJSON = await mapServiceRef.current.fetchGeoJSON(`${REACT_APP_GEOJSON_LOCATION}/${geoJSONType}.json`);
        return geoJSON;
    };


    /**
     * Initiera karta och ladda distrikt
     * 
     * Detta är huvudfunktionen som körs när Google Maps API är laddat.
     * Den gör två huvudsakliga saker:
     * 1. Initierar kartan (map operations, drawing manager, hämtar data)
     * 2. Laddar det aktuella distriktet från cache/state och renderar det på kartan
     */
    const initializeMapAndLoadDistrict = async (map, maps) => {

        setState(prevState => ({
            ...prevState,
            uiStates: {
                ...prevState.uiStates,
                mapIsLoading: true
            }
        }));

        // STEG 1: Initiera karta
        // ==========================================
        
        // Sätt type och scenarioId i map state
        mapOps.setTypeAndScenario(type, match.params.scenarioId);

        // Initialisera map operations
        await mapOps.initializeMap({
            map,
            maps,
            onAreaClick: (feature) => {
                selectArea(feature, true);
            }
        });
        
        // Initialisera drawing manager med batch-callback
        await drawingOps.initializeDrawingManager({
            map,
            maps,
            selectAreasBatch
        });
        
        // Ladda initial data från API (units och distrikt) och spara i cache
        await loadInitialData({
            unitsService: unitsServiceRef.current,
            mapService: mapServiceRef.current,
            type,
            match
        });

        // Hämta classifications från API vid initiering
        try {
            const res = await fetch(`${REACT_APP_API_BASE}/salesdistrict/classification/${match.params.scenarioId}`);
            const classifications = await res.json();
            districtCache.setClassifications(match.params.scenarioId, Array.isArray(classifications) ? classifications : []);
        } catch (error) {
            console.error('Failed to load classifications:', error);
            districtCache.setClassifications(match.params.scenarioId, []);
        }

        // Ladda distrikt från cache/state och rendera på kartan
        const districtId = match.params.id;
        const initialDistrictState = districtCache.get(districtId);

        // Parsa urvalslager från initial district state
        const parsedLayers = parseSelectedLayers(initialDistrictState);
        const parsedLayersKeys = Object.keys(parsedLayers).filter(key => parsedLayers[key].length > 0);
        
        // Hämta och beräkna alla värden som ska sparas
        // getSelectedAreasForDistrict laddar automatiskt alla nödvändiga layers
        const selectedAreasToUse = await getSelectedAreasForDistrict(initialDistrictState, parsedLayers, parsedLayersKeys);
        const selectionType = calculateSelectionType(parsedLayersKeys);
        
        // Spara district state i cache
        const districtState = districtCache.saveCurrent({
            ...initialDistrictState,
            isScheduledForSpatialRebuild: false,
            selectionType,
            selectedAreas: selectedAreasToUse || []
        });
        
        // Rendera uvalslagret (eller rensa det om selectionType är 'clear')
        // Detta säkerställer att tidigare urvalslager tas bort när man byter distrikt
        // Clear current selection first
        await renderFeaturesByType('clear');

        // Render new selection
        await renderFeaturesByType(selectionType);
    
        // Applicera district changes även om selectedAreas är tom
        // Detta säkerställer att units renderas även när selectionType är 'clear'
        // Använd tom array om selectedAreasToUse är null/undefined
        await applyDistrictChanges(selectedAreasToUse || [], true, null, true);
        
        // Hämta uppdaterad district state efter applyDistrictChanges
        const updatedDistrictState = districtCache.get(districtId);
        
        // Deaktivera "Placera hus"-verktyget när man byter distrikt
        // Detta säkerställer att knappen inte är aktiv när man byter distrikt
        drawingOps.toggleDrawHouse(false, null, null);
        
        // Uppdatera toolbar-komponentens state så att knappen visuellt också deaktiveras
        const mapState = mapOps.getMapState();
        if (mapState && mapState.tools && mapState.tools.resetTools) {
            mapState.tools.resetTools();
        }
        
        // Rensa houseMarkerRef för att säkerställa att vi inte har referens till gamla distriktets home marker
        deleteHomeMarkerForDistrict();
        
        // Skapa home marker för det aktuella distriktet (om den finns)
        createHomeMarkerForDistrict(updatedDistrictState);
        
        // Zoom till distrikt om det finns ytor, annars använd default position
        // Använd updatedDistrictState istället för districtState eftersom den är mer uppdaterad
        const hasAreas = updatedDistrictState?.spatialGeoJson && 
                        (typeof districtState.spatialGeoJson === 'string' ? 
                            (districtState.spatialGeoJson.trim() !== '' && districtState.spatialGeoJson !== '[]') : 
                            (Array.isArray(districtState.spatialGeoJson) && districtState.spatialGeoJson.length > 0));
        
        if (hasAreas && parsedLayersKeys.length > 0) {
            await setupDistrictZoom(parsedLayers, parsedLayersKeys);
        } else {
            setDefaultMapPosition();
        }
        
        // Bygg spatialGeoJson för alla distrikt i bakgrunden (efter att distriktet är laddat)
        buildSpatialGeoJsonForAllDistricts(
            (updatedDistrict, { spatialGeoJson }) => {
                const currentDistrictInCache = districtCache.get(updatedDistrict.id);
                districtCache.set(updatedDistrict.id, {
                    ...currentDistrictInCache,
                    spatialGeoJson
                });
            },
            // Callback när ALLA distrikt är klara — vänta på map idle + labels innan UI låses upp
            () => {
                const districtId = match.params.id;
                const scenarioId = match.params.scenarioId;

                renderSalesDistricts(districtId);
                mapOps.forceApplyStyles();

                const currentDistrict = districtCache.get(districtId);
                if (currentDistrict && currentDistrict.homeMarkerGeoJson) {
                    createHomeMarkerForDistrict(currentDistrict);
                }

                mapOps.runAfterMapIdle(() => {
                    if (String(match.params.id) !== String(districtId) ||
                        String(match.params.scenarioId) !== String(scenarioId)) {
                        return;
                    }
                    loadedDistrictIdRef.current = districtId;
                    loadedScenarioIdRef.current = scenarioId;
                    setState(prevState => ({
                        ...prevState,
                        uiStates: {
                            ...prevState.uiStates,
                            mapIsLoading: false
                        }
                    }));
                    districtCache.notifySubscribers();
                });
            }
        );
    };
    
 
    /**
     * Handle API loaded - kör initializeMapAndLoadDistrict när Google Maps API är redo
     * Denna useEffect körs både när match.params.id ändras OCH när kartan blir redo
     */
    useEffect(() => {
        const map = mapOps.getMap();
        const maps = mapOps.getMaps();
        if (map && maps) {
            initializeMapAndLoadDistrict(map, maps);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [match.params.id, match.params.scenarioId]);
    
    
    /**
     * Handle undo state
     */
    const handleUndoState = () => {
        const history = mapOps.getHistory();
        setState(prevState => ({
            ...prevState,
            undoIsActive: history.length > 0
        }));
    };
    
    /**
     * Undo
     */
    const undo = () => {
        const undoIsActive = mapOps.undo();
        setState(prevState => ({
            ...prevState,
            undoIsActive
        }));
    };

    /**
     * Select searched area
     */
    const selectSearchedArea = async (searchObj) => {
        
        const currentDistrict = districtCache.get(match.params.id);
        districtCache.saveCurrent({
            ...currentDistrict,
            selectionType: searchObj.type
        });

        setState(prevState => ({
            ...prevState,
            uiStates: {
                ...prevState.uiStates,
                mapIsLoading: true
            }
        }));

        await renderFeaturesByType('clear');
        await renderFeaturesByType(searchObj.type);


        setState(prevState => ({
            ...prevState,
            uiStates: {
                ...prevState.uiStates,
                mapIsLoading: false
            }
        }));

        const turfPolygons = mapOps.getTurfPolygons(searchObj.type);
        const data = turfPolygons?.find(data => data.feature.properties.id === searchObj.id);
        selectArea(data.feature, false);
        mapOps.zoomToDistrict([searchObj.id]);
        
        // Använd applyFilters för att säkerställa fullständig uppdatering (cache, state, units)
        const district = districtCache.get(match.params.id);
        await applyFilters(district.filteredUnits, district.salesDistrictFilters || null);
    };
    
    /**
     * Render features by type
     */
    const renderFeaturesByType = async (type) => {
        // Kolla om det finns synliga sales districts
        const allDistricts = districtCache.getAllDistrictsForScenario();
        const hasVisibleSalesDistricts = allDistricts && allDistricts.length > 0 && 
            allDistricts.some(district => district.visible === true);
        
        await renderSelectionTypeFeatures({
            type,
            fetchGeoJSON,
            isSalesDistricts: hasVisibleSalesDistricts || false
        });


    };
    
    /**
     * Handle selection change (from toolbar)
     */
    const handleSelectionChange = async (e) => {

        const type = e.currentTarget.value;
        const currentDistrict = districtCache.get(match.params.id);
        districtCache.saveCurrent({
            ...currentDistrict,
            selectionType: type
        });

        setState(prevState => ({
            ...prevState,
            uiStates: {
                ...prevState.uiStates,
                mapIsLoading: true
            }
        }));

        // Clear current selection first
        await renderFeaturesByType('clear');
        
        // Render new selection
        await renderFeaturesByType(type);

        setState(prevState => ({
            ...prevState,
            uiStates: {
                ...prevState.uiStates,
                mapIsLoading: false
            }
        }));
        districtCache.notifySubscribers();

        // Applicera styles när selection type ändras
        mapOps.applyStyles();
    };
    
    /**
     * Apply filters for current district
     */
    const applyFilters = async (units, salesDistrictFilters) => {
        // Hämta nuvarande distrikt från cache
        const district = districtCache.get(match.params.id);
        
        // Uppdatera salesDistrictFilters i cache
        districtCache.saveCurrent({ 
            ...district, 
            salesDistrictFilters,
            isDirty: true // Sätt isDirty när filtren ändras
        });
        
        // Använd befintliga selectedAreas och anropa applyDistrictChanges
        // forceIsDirty = true eftersom filtren har ändrats (inte selectedAreas)
        const selectedAreas = district.selectedAreas || [];
        await applyDistrictChanges(selectedAreas, true, true, true);
    }

    /**
     * Replace active district filters with filters from another district
     */
    const applyFiltersFromDistrict = async (sourceDistrictId) => {
        const activeDistrictId = match.params.id ? parseInt(match.params.id, 10) : null;
        const sourceDistrictIdNum = sourceDistrictId ? parseInt(sourceDistrictId, 10) : null;

        if (!activeDistrictId || !sourceDistrictIdNum || activeDistrictId === sourceDistrictIdNum) {
            return;
        }

        const sourceDistrict = districtCache.get(sourceDistrictIdNum);
        const activeDistrict = districtCache.get(activeDistrictId);

        if (!sourceDistrict || !activeDistrict) {
            return;
        }

        const sourceFilters = sourceDistrict.salesDistrictFilters === undefined
            ? null
            : sourceDistrict.salesDistrictFilters;
        let copiedFilters = null;
        if (sourceFilters !== null) {
            const clonedFilters = JSON.parse(JSON.stringify(sourceFilters));
            if (clonedFilters && typeof clonedFilters === 'object') {
                const { unitsIncludeExclude, ...filtersWithoutIncludeExclude } = clonedFilters;
                copiedFilters = filtersWithoutIncludeExclude;
            }
        }

        districtCache.saveCurrent({
            ...activeDistrict,
            salesDistrictFilters: copiedFilters,
            isDirty: true
        });

        districtCache.notifySubscribers();

        const selectedAreas = Array.isArray(activeDistrict.selectedAreas) ? activeDistrict.selectedAreas : [];
        await applyDistrictChanges(selectedAreas, true, true, true);
    }

    /**
     * Toggle sale districts
     */
    const toggleAllSaleDistricts = async (isVisible) => {
        const activeDistrictId = match.params.id ? parseInt(match.params.id, 10) : null;
        const allDistricts = districtCache.getAllDistrictsForScenario();

        for (const district of allDistricts) {
            // Aktiva distriktet ska alltid kunna selectera ytor – sätt därför inte visible: false på det vid "dölj alla"
            const visible = isVisible ? true : (district.id === activeDistrictId ? true : false);
            districtCache.set(district.id, {
                ...district,
                visible
            });
        }

        districtCache.notifySubscribers();

        const currentDistrict = districtCache.get(match.params.id);
        renderSalesDistricts(currentDistrict?.id || null);
    }
    
    const toggleSaleDistrict = async (districtId, isVisible) => {
        districtCache.set(districtId, {
            ...districtCache.get(districtId),
            visible: isVisible
        });

        districtCache.notifySubscribers();

        const currentDistrict = districtCache.get(match.params.id);
        renderSalesDistricts(currentDistrict?.id || null);
    };

    const updateDistrictNameAndColor = async (districtId, name, color) => {
        const district = districtCache.get(districtId);
        districtCache.set(districtId, {
            ...district,
            name,
            color,
            isDirty: true
        });

        const isActiveDistrict = districtId === parseInt(match.params.id);
        
        if (isActiveDistrict) {
            // Uppdatera färgen på selectedAreas för det aktiva distriktet
            mapOps.forceApplyStyles();
            
            // Spara home marker info innan renderSalesDistricts (som kan ta bort den)
            const hadHomeMarker = houseMarkerRef.current !== null;
            const homeMarkerGeoJson = hadHomeMarker ? houseMarkerRef.current.getGeoJSON() : null;
            
            // Uppdatera labels och andra distrikt
            renderSalesDistricts(match.params.id);
            
            // Återskapa home marker med ny färg om den fanns
            if (hadHomeMarker && homeMarkerGeoJson) {
                const updatedDistrict = districtCache.get(districtId);
                createHomeMarkerForDistrict(updatedDistrict);
            }
            // Only pin colors change -- update in-place without full rebuild
            const updatedDistrictState = districtCache.get(districtId);
            if (updatedDistrictState) {
                unitsOps.refreshPinColors(updatedDistrictState.units);
            }
        } else {
            // Uppdatera labels och andra distrikt (home markers för icke-aktiva distrikt uppdateras här)
            renderSalesDistricts(match.params.id);
        }
        
        districtCache.notifySubscribers();
    };

    const deleteDistrict = async (districtId) => {
        try {
            const rawResponse = await fetch(`${REACT_APP_API_BASE}/salesdistrict/delete/${districtId}`, {
                method: 'DELETE'
            });

            if (!rawResponse.ok) {
                throw new Error('Failed to delete district');
            }

            const isActiveDistrict = districtId === parseInt(match.params.id);

            districtCache.remove(districtId);
            renderSalesDistricts(isActiveDistrict ? null : match.params.id);
            
            districtCache.notifySubscribers();

            const visibility = state.uiStates.unitsVisibility;
            if (visibility === UnitsVisibility.ALL) {
                if (isActiveDistrict) {
                    unitsOps.refreshPinColors(null);
                } else {
                    const activeDistrict = districtCache.get(match.params.id);
                    const unitIdsForActive =
                        activeDistrict && Array.isArray(activeDistrict.units)
                            ? activeDistrict.units
                            : null;
                    unitsOps.refreshPinColors(unitIdsForActive);
                }
            } else if (
                !isActiveDistrict
                && (visibility === UnitsVisibility.AREA || visibility === UnitsVisibility.AREA_STRICT)
            ) {
                const currentDistrict = districtCache.get(match.params.id);
                if (currentDistrict) {
                    await applyDistrictChanges(currentDistrict.selectedAreas, true, null, true);
                }
            }

            if (isActiveDistrict) {
                const remainingDistricts = districtCache.getAllDistrictsForScenario();
                const scenarioId = match.params.scenarioId;
                const prefix = type === 'foodservice' ? '/foodservice-scenario' : '/scenario';
                if (remainingDistricts.length > 0) {
                    history.push(`${prefix}/${scenarioId}/create/${remainingDistricts[0].id}`);
                } else {
                    history.push(`${prefix}/${scenarioId}`);
                }
            }
        } catch (error) {
            console.error(`Failed to delete district ${districtId}:`, error);
            alert('Kunde inte radera distrikt. Försök igen.');
            throw error;
        }
    };
    
    /**
     * Toggle units
     */
    const toggleUnits = async (unitsVisible) => {
        unitsOps.setVisibilityMode(unitsVisible);
        const currentDistrict = districtCache.get(match.params.id);
        await applyDistrictChanges(currentDistrict.selectedAreas, true, null, true);
        
        setState(prevState => ({
            ...prevState,
            uiStates: {
                ...prevState.uiStates,
                unitsVisibility: unitsVisible
            }
        }));
    };
    
    /**
     * Has selection type
     */
    const hasSelectionType = (type) => {
        // Hämta district types från det nuvarande distriktets parsed layers
        const currentDistrict = districtCache.get(match.params.id);
        if (!currentDistrict) {
            return false;
        }
        
        const parsedLayers = parseSelectedLayers(currentDistrict);
        const parsedLayersKeys = Object.keys(parsedLayers).filter(key => parsedLayers[key].length > 0);
        
        return parsedLayersKeys.includes(type);
    };
    
    /**
     * Save district
     */
    const save = async () => {
        setState(prevState => ({
            ...prevState,
            isLoading: true,
            uiStates: {
                ...prevState.uiStates,
                isSaving: true,
                mapIsLoading: true
            }
        }));

        const allDistricts = districtCache.getAllDistrictsForScenario();
        
        // Bygg spatialGeoJson för alla distrikt som behöver det innan sparning
        // Vänta tills alla är klara innan vi börjar spara
        await new Promise((resolve) => {
            buildSpatialGeoJsonForAllDistricts(
                // Callback för varje distrikt när det är klart
                (updatedDistrict, { spatialGeoJson }) => {
                    const currentDistrictInCache = districtCache.get(updatedDistrict.id);
                    districtCache.set(updatedDistrict.id, {
                        ...currentDistrictInCache,
                        spatialGeoJson,
                        isScheduledForSpatialRebuild: false
                    });
                },
                // Callback när ALLA distrikt är klara
                () => {
                    resolve();
                }
            );
        });
        
        // Hämta uppdaterade distrikt efter spatialGeoJson är byggt
        const updatedAllDistricts = districtCache.getAllDistrictsForScenario();
        const saveResults = [];
        const saveErrors = [];  
        
        // Spara alla distrikt sekventiellt med felhantering
        for (const district of updatedAllDistricts) {
            if (!district.isDirty) {
                continue;
            }

            try {
                // Ta bort calculations från district-objektet innan sparning (de hämtas från cache i saveSingleDistrict)
                const { 
                    isScheduledForSpatialRebuild,
                    isDirty,
                    turnOver,
                    salesArea,
                    salesDistrictArea,
                    numberOfHits,
                    frequency,
                    vistingTimeHours,
                    ...saveDistrictObject } = district;
                const result = await saveSingleDistrict(saveDistrictObject);
                saveResults.push({ districtId: district.id, result });
               
            } catch (error) {
                console.error(`Failed to save district ${district.id}:`, error);
                saveErrors.push({ districtId: district.id, error });
            }
        }
        
        // Uppdatera state baserat på resultat
        if (saveErrors.length === 0) {
            setState(prevState => ({
                ...prevState,
                isLoading: false,
                uiStates: {
                    ...prevState.uiStates,
                    isSaving: false,
                    mapIsLoading: false,
                    isFinishedSaving: true
                }
            }));
            districtCache.notifySubscribers();

            setTimeout(() => {
                setState(prevState => ({
                    ...prevState,
                    uiStates: {
                        ...prevState.uiStates,
                        isFinishedSaving: false
                    }
                }));
            }, 2500);

        } else if (saveResults.length > 0) {
            setState(prevState => ({
                ...prevState,
                isLoading: false,
                uiStates: {
                    ...prevState.uiStates,
                    isSaving: false,
                    mapIsLoading: false
                }
            }));
            districtCache.notifySubscribers();
            console.error(`Failed to save ${saveErrors.length} out of ${allDistricts.length} districts:`, saveErrors);
            alert(`Kunde inte spara ${saveErrors.length} av ${allDistricts.length} distrikt. Kontrollera konsolen för detaljer.`);
        } else {
            setState(prevState => ({
                ...prevState,
                isLoading: false,
                uiStates: {
                    ...prevState.uiStates,
                    isSaving: false,
                    mapIsLoading: false
                }
            }));
            districtCache.notifySubscribers();
            console.error('Failed to save all districts:', saveErrors);
        }
        
        return;
    };
    
    /**
     * Save single district
     * @param {Object} district - District object to save
     */
    const saveSingleDistrict = async (district) => {
        try {
            // Hämta distriktet från cache för att få calculations (om det finns)
            // För nya distrikt kan updatedDistrict vara null, men calculations kommer från district-parametern
            const updatedDistrict = districtCache.get(district.id);
            const districtWithCalculations = updatedDistrict || district;
            
            // Extrahera calculations från cache (eller från district om det är ett nytt distrikt)
            const calculations = {
                turnOver: districtWithCalculations.turnOver,
                salesArea: districtWithCalculations.salesArea,
                salesDistrictArea: districtWithCalculations.salesDistrictArea,
                numberOfHits: districtWithCalculations.numberOfHits,
                frequency: districtWithCalculations.frequency,
                vistingTimeHours: districtWithCalculations.vistingTimeHours
            };
            
            // Konvertera salesDistrictArea från m² till km² (API:et förväntar sig km² som heltal)
            const calculationsForApi = {
                ...calculations,
                salesDistrictArea: calculations.salesDistrictArea 
                    ? Math.round(calculations.salesDistrictArea / 1000000) 
                    : 0
            };
            
            const result = await mapServiceRef.current.saveDistrict({
                ...district,
                ...calculationsForApi, // Inkludera calculations med konverterad salesDistrictArea
                spatialGeoJson: district.spatialGeoJson ? JSON.stringify(district.spatialGeoJson) : '',
                homeMarkerGeoJson: district.homeMarkerGeoJson ? JSON.stringify([district.homeMarkerGeoJson]) : ''
            }, district.id);
            
            // Hämta units från API (på samma sätt som i loadInitialData)
            const units = await unitsServiceRef.current.fetchUnits(type);
            
            // Hämta det uppdaterade distriktet från cache igen (efter API-anropet) för att få senaste selectedAreas och filters
            const finalUpdatedDistrict = districtCache.get(district.id);
            
            // Spara distriktet i cache med calculations och uppdaterade units
            // Sätt visible: true om det inte finns, men behåll false om det redan är satt till false
            const districtToSave = finalUpdatedDistrict || district;
            const visibleValue = districtToSave.visible !== undefined 
                ? districtToSave.visible 
                : true; // Sätt till true om visible saknas
            
            // Use set() not saveCurrent() so bulk save does not move currentDistrictId
            // to the last saved district (area clicks use getCurrentDistrictId()).
            districtCache.set(result.id, {
                ...districtToSave,
                id: result.id,
                isDirty: false,
                allUnits: units,
                visible: visibleValue,
                ...calculations, // Spara calculations från cache
            });
    
            return result;    
        } catch (error) {
            console.error(`Failed to save district ${district.id}:`, error);
            throw error;
        }
    };

    const urlMatchesLoaded =
        loadedDistrictIdRef.current !== null &&
        String(loadedDistrictIdRef.current) === String(match.params.id) &&
        String(loadedScenarioIdRef.current) === String(match.params.scenarioId);

    const mapUiLoading = state.uiStates.mapIsLoading || !urlMatchesLoaded;
 
    return {
        // State
        state,
        mapUiLoading,
        
        // Handlers
        handlers: {
            onApiLoaded: initializeMapAndLoadDistrict, 
            selectArea,
            selectSearchedArea,
            hasSelectionType,
            renderFeaturesByType,
            handleSelectionChange,
            applyFilters,
            applyFiltersFromDistrict,
            toggleAllSaleDistricts,
            toggleSaleDistrict,
            toggleUnits,
            undo,
            save,
            saveSingleDistrict,
            updateDistrictNameAndColor,
            deleteDistrict,
            // Additional handlers
            drawPolygon: () => drawingOps.drawPolygon(),
            drawCircle: () => drawingOps.drawCircle(),
            stopDrawing: () => drawingOps.stopDrawing(),
            toggleDrawHouse: (isToolActive) => {
                drawingOps.toggleDrawHouse(isToolActive, houseMarkerRef.current, handleChangeHouseMarker)
            },
            getSelectionType: () => {
                const district = districtCache.get(match.params.id);
                return district?.selectionType || 'clear';
            },
            getSelectedAreas: () => {
                const district = districtCache.get(match.params.id);
                return district?.selectedAreas || [];
            },
            addTools: (tools) => mapOps.setTools(tools)
        },
    };
}
